import { useEffect, useRef, type MouseEvent, type ReactNode } from 'react';
import { useContentRect } from '../AnnotationDraft/DrawingLayer/hooks/useContentRect';
import { drawItems } from '../../utils/markupRender';
import { topmostHitIndex } from '../../utils/markupHitTest';
import { resolveMarkupColor, type MarkupItem } from '../../utils/markupTypes';
import { ScrollArea } from '@components/ui/ScrollArea/ScrollArea';
import styles from './AnnotationCanvas.module.css';

/** How close the pointer must be to a mark to reveal who drew it. */
const HOVER_TOLERANCE_PX = 6;

/** One painted mark and the annotation it belongs to — attribution is a
 *  property of the annotation, never of the item (ADR-0013). */
export interface IPaintedItem {
  item: MarkupItem;
  annotationId: string;
}

export interface IHoveredMark {
  annotationId: string;
  x: number;
  y: number;
}

interface IAnnotationCanvasProps {
  children: ReactNode; // The rendered document — an `<img>` or the pdf page `<canvas>`
  /** Re-measures when this changes: a page flip resizes the same canvas. */
  contentKey: string | number;
  painted: readonly IPaintedItem[];
  onHover: (hovered: IHoveredMark | null) => void;
}

/**
 * The read-only half of the editor: the document with the checked annotations
 * over it. It shares the editor's geometry — `useContentRect`, `drawItems` — so
 * a mark lands identically here, in the drawing layer, and in the download.
 */
export function AnnotationCanvas({ children, contentKey, painted, onHover }: IAnnotationCanvasProps) {
  const stageRef = useRef<HTMLDivElement>(null);
  const overlayRef = useRef<HTMLCanvasElement>(null);
  const contentRect = useContentRect(stageRef, contentKey);

  const items = painted.map((p) => p.item);

  useEffect(() => {
    const overlay = overlayRef.current;
    if (!overlay || !contentRect) return;
    const ctx = overlay.getContext('2d');
    if (!ctx) return;
    const dpr = window.devicePixelRatio || 1;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, contentRect.width, contentRect.height);
    drawItems(ctx, items, contentRect.width, contentRect.height, resolveMarkupColor);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [painted, contentRect]);

  // Hovering over a mark reveals who drew it, so the page can show a tooltip.
  function handleMove(event: MouseEvent<HTMLCanvasElement>) {
    if (!contentRect) return;
    const rect = event.currentTarget.getBoundingClientRect();
    const point = {
      x: ((event.clientX - rect.left) / rect.width) * 100,
      y: ((event.clientY - rect.top) / rect.height) * 100,
    };
    const index = topmostHitIndex(items, point, contentRect.width, contentRect.height, HOVER_TOLERANCE_PX);
    const hit = index === null ? null : painted[index];
    onHover(hit ? { annotationId: hit.annotationId, x: event.clientX, y: event.clientY } : null);
  }

  return (
    <ScrollArea orientation="both" className={styles.stage} viewportClassName={styles.stageBody} viewportRef={stageRef}>
      {children}
      {contentRect && (
        <canvas
          ref={overlayRef}
          data-overlay
          className={styles.overlay}
          width={Math.round(contentRect.width * (window.devicePixelRatio || 1))}
          height={Math.round(contentRect.height * (window.devicePixelRatio || 1))}
          style={{
            left: contentRect.left,
            top: contentRect.top,
            width: contentRect.width,
            height: contentRect.height,
          }}
          onMouseMove={handleMove}
          onMouseLeave={() => onHover(null)}
        />
      )}
    </ScrollArea>
  );
}
